(ns ui.editors.entity-creator.copy-config
  "Secondary dropdown-button rendered inside the creator-config modal, one
   slot per click. Given the current pipeline + a `config-path`, offers other
   pipelines (same entity-type) whose parsed `:pipeline/creator-config` has a
   saved value at that path. Selecting one runs the shared cljc copy helpers
   to translate the source subtree into the current pipeline's shape (stage
   IDs, field permissions, associations) and dispatches
   `m/replace-config-at-path` to write it into the modal's edit overlay.
   Nothing is persisted until Save."
  (:require
   [clojure.string :as string]
   [common.localization :as loc]
   [common.schema.entity-creator.copy-config :as copy]
   [common.schema.entity-type-names :as etn]
   [common.util :as cu]
   [frontend.state.current-session :as session]
   [re-frame.core :as rf]
   [ui.components.radix-ui.dropdown-menu.view :as dropdown-menu]
   [ui.components.radix-ui.tooltip.view :as tooltip]
   [ui.editors.entity-creator.model :as m]
   [ui.util :as uu]))

(def pipelines-reaction-by-entity-type
  {:entity.type/oppo session/oppo-pipelines
   :entity.type/fundraising-opportunity session/froppo-pipelines
   :entity.type/sales-opportunity session/sales-oppo-pipelines
   :entity.type/exit-opportunity session/exit-oppo-pipelines})

(defn candidate-pipelines
  "Non-disabled pipelines that share the current pipeline's entity-type
   (excluding the current one) whose creator-config has a saved value at
   `config-path`. Active pipelines first, then by trimmed lower-case name."
  [{:keys [pipeline config-path]}]
  (let [entity-type (:pipeline/entity-type pipeline)
        current-id (:db/id pipeline)
        pipelines (some-> (get pipelines-reaction-by-entity-type entity-type) deref)]
    (->> pipelines
         (remove (fn [p]
                   (= current-id (:db/id p))))
         (filter (fn [p]
                   (some? (get-in (:pipeline/creator-config p) config-path))))
         (sort-by (juxt (fn [p]
                          (boolean (:pipeline/inactive? p)))
                        (fn [p]
                          (cu/str-trim-lcase (:pipeline/name p)))))
         vec)))

(defn disabled-tooltip-content
  [{:keys [config-path target-type]}]
  (let [slot (copy/path-slot config-path)]
    (case slot
      (:main-config :single-entity-config)
      (if target-type
        (loc/tr :creator-config/copy-disabled-opportunity-with-target
                [(-> target-type
                     (etn/get-plural :en)
                     string/lower-case)])
        (loc/tr :creator-config/copy-disabled-opportunity-no-target))

      :target-config
      (loc/tr :creator-config/copy-disabled-target
              [(-> target-type
                   (etn/get-name :en)
                   string/lower-case)])

      :target-profile-config
      (loc/tr :creator-config/copy-disabled-target-profile
              [(-> target-type
                   (etn/get-name :en)
                   string/lower-case)])

      :associations-config
      (if target-type
        (loc/tr :creator-config/copy-disabled-associations-with-target
                [(-> target-type
                     (etn/get-plural :en)
                     string/lower-case)])
        (loc/tr :creator-config/copy-disabled-associations-no-target)))))

(defn perform-copy!
  [{:keys [src-pipeline tgt-pipeline config-path]}]
  (let [src-cfg (:pipeline/creator-config src-pipeline)
        src-stages (or (session/pipeline-stages src-pipeline) [])
        tgt-stages (or (session/pipeline-stages tgt-pipeline) [])
        {:keys [stage-map]} (copy/build-stage-id-map
                             {:src-stages src-stages
                              :tgt-stages tgt-stages
                              :src-name-attr (copy/stage-name-attribute src-pipeline)
                              :tgt-name-attr (copy/stage-name-attribute tgt-pipeline)})
        {:keys [tgt-config]} (copy/compute-new-creator-config
                              {:src-config src-cfg
                               :tgt-config {}
                               :paths [config-path]
                               :stage-map stage-map
                               :src-pipeline src-pipeline
                               :tgt-pipeline tgt-pipeline})
        new-subtree (get-in tgt-config config-path)]
    ;; Defensive: applicable-path? in the cljc reducer can skip the path
    ;; (target-type-not-supported, missing-in-source, etc), leaving tgt-config
    ;; untouched and new-subtree nil. UI gating should already prevent this,
    ;; but avoid clearing the edit overlay's slot if the compute silently
    ;; no-ops.
    (when (some? new-subtree)
      (rf/dispatch [m/replace-config-at-path
                    {:config-path config-path
                     :new-config new-subtree}]))))

(def trigger-base-class
  "m-0 px-5 py-3 inline-flex gap-2 items-center outline-none font-muli font-semibold
   text-md leading-snug rounded underline-offset-4 whitespace-nowrap select-none
   border border-solid")

(def trigger-enabled-class
  "cursor-pointer text-secondary-light-text hover:text-secondary-light-accent
   border-secondary-light-border hover:bg-secondary-light")

(def trigger-disabled-class
  "cursor-not-allowed text-disabled-regular-text border-disabled-regular-border bg-regular")

(defn trigger-span
  [{:keys [disabled?]}]
  [:span
   {:class (uu/cm trigger-base-class
                  (if disabled?
                    trigger-disabled-class
                    trigger-enabled-class))}
   [:i {:class (uu/cm "fal fa-info-circle")}]
   (loc/tr :creator-config/copy-from-another-pipeline)
   [:i {:class (uu/cm "fa fa-chevron-down")}]])

(defn dropdown-items
  [{:keys [candidates src-pipeline-args]}]
  (mapv (fn [p]
          {:label (:pipeline/name p)
           :key (str "copy-src-" (:db/id p))
           :on-click (fn [_]
                       (perform-copy!
                        (assoc src-pipeline-args :src-pipeline p)))})
        candidates))

(defn trigger-with-tooltip
  [{:keys [disabled? tooltip-content]}]
  [tooltip/view
   {:side "top"}
   [trigger-span {:disabled? disabled?}]
   tooltip-content])

(defn view
  "Props:
     :pipeline    current (target) pipeline entity
     :target-type current tab's target-type (nil when no target)
     :config-path path into :pipeline/creator-config for the current slot"
  [{:keys [pipeline target-type config-path]}]
  (let [candidates (candidate-pipelines {:pipeline pipeline
                                         :config-path config-path})
        disabled? (empty? candidates)
        tooltip-content (if disabled?
                          (disabled-tooltip-content {:config-path config-path
                                                     :target-type target-type})
                          (loc/tr :creator-config/copy-info-tooltip))]
    (if disabled?
      [trigger-with-tooltip {:disabled? true
                             :tooltip-content tooltip-content}]
      [dropdown-menu/view
       {:trigger [trigger-with-tooltip {:disabled? false
                                        :tooltip-content tooltip-content}]
        :items (dropdown-items
                {:candidates candidates
                 :src-pipeline-args {:tgt-pipeline pipeline
                                     :config-path config-path}})
        :props {:align "start"
                :side-offset 5}}])))
